import React, { useState } from "react";
import PropTypes from "prop-types";

import { Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

import MDBox from "components/MDBox";

/**
 * Drag-and-drop reordering for the ordered lists in the panel — page sections,
 * the rows inside a section, brand-page paragraph lists and the product table.
 *
 * Built on the browser's native drag events rather than a library: the panel had
 * no drag dependency, and a vertical list needs little more than this.
 *
 * Two rules make it behave well next to form fields and nested lists:
 *
 *  1. Only the handle is `draggable`, never the row. A draggable row swallows
 *     text selection inside its inputs.
 *  2. Every list declares a `group`. A row ignores drags from another group, so
 *     a row dragged inside a section can never drop into the section list that
 *     surrounds it.
 */

// Which list and row the pointer picked up. Module-scoped rather than React
// state because dataTransfer contents cannot be read during dragover in most
// browsers, and a nested list has to know whether the drag belongs to it.
let activeDrag = null;

export const beginDrag = (group, index) => {
  activeDrag = { group, index };
};

export const readDrag = (group) =>
  activeDrag && activeDrag.group === group ? activeDrag.index : null;

export const endDrag = () => {
  activeDrag = null;
};

/**
 * Moves one entry to an arbitrary position, shifting everything between.
 *
 * This is what makes a drag differ from the up/down arrows: the arrows swap two
 * neighbours, while a drag lifts a row out and reinserts it, so the rows it
 * passed over keep their relative order.
 */
export const moveItem = (list, from, to) => {
  if (
    from === to ||
    from < 0 ||
    to < 0 ||
    from >= list.length ||
    to >= list.length
  ) {
    return list;
  }

  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const rowStyle = (isDragging, isOver) => ({
  opacity: isDragging ? 0.4 : 1,
  outline: isOver ? "2px dashed #1A73E8" : "2px dashed transparent",
  outlineOffset: 2,
  borderRadius: 8,
  transition: "opacity 120ms ease, outline-color 120ms ease",
});

/** The grip the user actually drags. */
export const DragHandle = ({ label, disabled, ...rest }) => (
  <Tooltip title={disabled ? "" : label}>
    <MDBox
      component="span"
      display="inline-flex"
      alignItems="center"
      sx={{
        cursor: disabled ? "default" : "grab",
        color: disabled ? "text.disabled" : "text.secondary",
        "&:active": { cursor: disabled ? "default" : "grabbing" },
      }}
      {...rest}
    >
      <DragIndicatorIcon fontSize="small" />
    </MDBox>
  </Tooltip>
);

DragHandle.defaultProps = { label: "Drag to reorder", disabled: false };
DragHandle.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
};

/**
 * Renders `count` rows, handing each one the props and handle it needs.
 *
 * `children` is called as (index, drag) and must spread `drag.rowProps` on the
 * row's root element and place `drag.handle` somewhere inside it.
 */
const DragList = ({ group, count, onReorder, disabled, children }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const clear = () => {
    setDragIndex(null);
    setOverIndex(null);
    endDrag();
  };

  // True when the in-flight drag started in this list.
  const isMine = () => activeDrag !== null && activeDrag.group === group;

  const dragFor = (index) => ({
    isDragging: dragIndex === index,
    isOver: overIndex === index && dragIndex !== index,

    rowProps: {
      style: rowStyle(dragIndex === index, overIndex === index && dragIndex !== index),

      onDragOver: (event) => {
        if (disabled || !isMine()) return;
        // Both are required for a drop to be allowed at all, and stopping here
        // keeps an enclosing list from also claiming this position.
        event.preventDefault();
        event.stopPropagation();
        if (overIndex !== index) setOverIndex(index);
      },

      onDragLeave: (event) => {
        if (disabled || !isMine()) return;
        event.stopPropagation();
        if (overIndex === index) setOverIndex(null);
      },

      onDrop: (event) => {
        if (disabled || !isMine()) return;
        event.preventDefault();
        event.stopPropagation();

        const from = activeDrag.index;
        clear();
        if (from !== index) onReorder(from, index);
      },
    },

    handle: (
      <DragHandle
        disabled={disabled}
        draggable={!disabled}
        onDragStart={(event) => {
          if (disabled) {
            event.preventDefault();
            return;
          }
          beginDrag(group, index);
          setDragIndex(index);
          event.dataTransfer.effectAllowed = "move";
          // Firefox refuses to start a drag without payload.
          event.dataTransfer.setData("text/plain", String(index));
        }}
        onDragEnd={clear}
      />
    ),
  });

  return (
    <>
      {Array.from({ length: count }, (unused, index) => children(index, dragFor(index)))}
    </>
  );
};

DragList.defaultProps = { disabled: false };

DragList.propTypes = {
  // Isolates this list from any other on the page, including its own parent.
  group: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  onReorder: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.func.isRequired,
};

export default DragList;
